import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const quickFill = (e, p) => { setEmail(e); setPassword(p); };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/patient";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      const dest =
        u.role === "admin" ? "/admin"
        : u.role === "doctor" ? "/doctor"
        : u.role === "partner" ? "/partner"
        : u.role === "driver" ? "/driver"
        : u.role === "field_executive" ? "/field"
        : "/patient";
      nav(dest);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="text-center mb-6">
        <img src="/logo-full.webp" alt="MedHOne — One Platform, Every Care" className="h-24 mx-auto object-contain" data-testid="login-brand-logo" />
      </div>
      <Card className="p-8 rounded-2xl">
        <h1 className="font-display text-3xl font-bold">Welcome back</h1>
        <p className="text-slate-600 text-sm mt-1">Sign in to your MedHOne account</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-500 text-white">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="my-6 border-t border-slate-200" />

        <Button type="button" variant="outline" onClick={googleSignIn} data-testid="google-signin"
          className="w-full border-slate-300 flex items-center justify-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.02c2.2-2 3.4-5 3.4-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.14.01-3.6 2.8-.05.13C3.4 21.3 7.4 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-.01-.16-3.7-2.8-.12.06C.5 8.3 0 10.1 0 12s.5 3.7 1.4 5.3l3.8-2.9z"/><path fill="#EA4335" d="M12 4.6c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.7l3.8 2.9c1-2.9 3.7-5 6.8-5z"/></svg>
          Continue with Google
        </Button>
        <p className="text-[11px] text-slate-500 text-center mt-2">New Google users join as patients. Existing accounts keep their role.</p>

        <div className="my-6 border-t border-slate-200" />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Quick demo login</div>
        <div className="grid grid-cols-3 gap-2">
          <Button data-testid="demo-patient" type="button" variant="outline" size="sm" onClick={() => quickFill("patient@medhone.in", "Patient@123")}>Patient</Button>
          <Button data-testid="demo-doctor" type="button" variant="outline" size="sm" onClick={() => quickFill("doctor@medhone.in", "Doctor@123")}>Doctor</Button>
          <Button data-testid="demo-admin" type="button" variant="outline" size="sm" onClick={() => quickFill("medhone1965@gmail.com", "MedHOne@2026")}>Admin</Button>
          <Button data-testid="demo-partner" type="button" variant="outline" size="sm" onClick={() => quickFill("partner@medhone.in", "Partner@123")}>Partner</Button>
          <Button data-testid="demo-driver" type="button" variant="outline" size="sm" onClick={() => quickFill("driver@medhone.in", "Driver@123")}>Driver</Button>
          <Button data-testid="demo-field" type="button" variant="outline" size="sm" onClick={() => quickFill("field@medhone.in", "Field@123")}>Field Exec</Button>
        </div>

        <div className="text-sm text-center text-slate-600 mt-6">
          New to MedHOne? <Link data-testid="go-register" className="text-teal-700 font-semibold" to="/register">Create an account</Link>
        </div>
      </Card>
    </div>
  );
}
